import { QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPermission } from 'api';
import { CustomPrimaryUser } from 'api/models/CustomPrimaryUser';
import { UsersService } from 'services';

const getUserDetails = async (userId: number) => {
  const usersService = new UsersService();
  const userDetails = await usersService.getUserProfileById(userId);
  if (!userDetails) {
    throw new Error('An error occurred while retrieving user details');
  }
  return userDetails;
};

const useGetUserDetails = (
  userId: number
): { data: CustomPrimaryUser; isLoading: boolean; error: unknown; refetch: () => Promise<unknown> } => {
  const { data, isLoading, error, refetch } = useQuery({
    enabled: !!userId,
    queryKey: ['userDetails', userId],
    queryFn: () => getUserDetails(userId),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
  return { data, isLoading, error, refetch };
};

const getUserPermissions = async (userId: number, firmId: number) => {
  const usersService = new UsersService();
  const userPermissions = await usersService.getUserPermissions(userId, firmId);
  if (!userPermissions) {
    throw new Error('An error occurred while retrieving user permissions');
  }
  return userPermissions;
};

const getFirmUserPermissions = async (firmId: number) => {
  const usersService = new UsersService();
  const userPermissions = await usersService.getFirmUserPermissions(firmId);
  if (!userPermissions) {
    throw new Error('An error occurred while retrieving user permissions');
  }
  return userPermissions;
};

const useGetUserPermissions = (
  userId: number,
  firmId: number
): { data: UserPermission[]; isLoading: boolean; error: unknown } => {
  const { data, isLoading, error } = useQuery({
    enabled: !!userId && !!firmId,
    queryKey: ['userPermissions', userId, firmId],
    queryFn: () => getUserPermissions(userId, firmId),
  });
  return { data, isLoading, error };
};

const updateUserProfile = async ({ userId, updatedUserProfile }: { userId: number; updatedUserProfile: unknown }) => {
  const usersService = new UsersService();
  const updatedUser = await usersService.updateUserProfile(userId, updatedUserProfile);
  if (!updatedUser) {
    throw new Error('An error occurred while updating user details');
  }
  return updatedUser;
};

const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  const updateUserProfileMutation = useMutation({
    mutationFn: ({ userId, updatedUserProfile }: { userId: number; updatedUserProfile: unknown }) =>
      updateUserProfile({
        userId,
        updatedUserProfile,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['firmUsers'],
        exact: false 
      });
      queryClient.invalidateQueries({ queryKey: ['firmUsersSlim'] as QueryKey});
    },
  });

  const { mutate, isPending, error, isSuccess } = updateUserProfileMutation;

  if (isSuccess) {
    updateUserProfileMutation.reset();
  }

  return { mutate, isLoading: isPending, error, isSuccess };
};

export { useGetUserDetails, useGetUserPermissions, useUpdateUserProfile, getUserPermissions, getFirmUserPermissions };
